import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

const Toast = ({ 
  message, 
  type = 'success', 
  isVisible, 
  onClose, 
  duration = 5000,
  position = 'top-center'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Esperar a que termine la animación
  }, [onClose]);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, handleClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '20px' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#f44336', fontSize: '20px' }} />;
      case 'info':
        return <InfoIcon sx={{ color: '#2196f3', fontSize: '20px' }} />;
      default:
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '20px' }} />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return '#e8f5e8';
      case 'error':
        return '#ffebee';
      case 'info':
        return '#e3f2fd';
      default:
        return '#e8f5e8';
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'info':
        return '#2196f3';
      default:
        return '#4caf50';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#c62828';
      case 'info':
        return '#1565c0';
      default:
        return '#2e7d32';
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top-center':
        return {
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'top-right':
        return {
          top: '20px',
          right: '20px',
        };
      case 'top-left':
        return {
          top: '20px',
          left: '20px',
        };
      case 'bottom-center':
        return {
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'bottom-right':
        return {
          bottom: '20px',
          right: '20px',
        };
      case 'bottom-left':
        return {
          bottom: '20px',
          left: '20px',
        };
      default:
        return {
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
        };
    }
  };

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 10000,
        maxWidth: { xs: 'calc(100vw - 40px)', sm: '400px' },
        width: { xs: 'calc(100vw - 40px)', sm: 'auto' },
        minWidth: '280px',
        backgroundColor: getBackgroundColor(),
        border: `1px solid ${getBorderColor()}`,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        padding: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        opacity: isAnimating ? 1 : 0,
        transform: isAnimating ? 'translateY(0)' : 'translateY(-20px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        ...getPositionStyles(),
      }}
    >
      {/* Icono */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexShrink: 0,
        marginTop: '2px'
      }}>
        {getIcon()}
      </Box>

      {/* Contenido del mensaje */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: getTextColor(),
            fontSize: { xs: '14px', sm: '15px' },
            fontWeight: '500',
            lineHeight: '1.4',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {message}
        </Typography>
      </Box>

      {/* Botón de cerrar */}
      <IconButton
        onClick={handleClose}
        sx={{
          color: getTextColor(),
          padding: '4px',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    </Box>
  );
};

export default Toast;
